// FeesSummary.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";

import logo from "./messiahlogo.png";

import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";

import api from "../../api/api";
import MonthlyAnalysic from "./MonthlyAnalysic";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// NOTE: Using html2pdf via dynamic import (no top-level import to keep bundle small)
const MONTHS = [
  { value: "january", label: "January" },
  { value: "february", label: "February" },
  { value: "march", label: "March" },
  { value: "april", label: "April" },
  { value: "may", label: "May" },
  { value: "june", label: "June" },
  { value: "july", label: "July" },
  { value: "august", label: "August" },
  { value: "september", label: "September" },
  { value: "october", label: "October" },
  { value: "november", label: "November" },
  { value: "december", label: "December" },
];

const FEE_TYPE_LABELS = {
  graduation: "Graduation Fee",
  entrance: "Entrance Fee",
  project: "Project Fee",
  activities: "Activities Fee",
  wacce: "WACCE Fee",
  extra_class: "Extra Class Fee",
  sale: "Sales",
  service_income: "Service Income",
  facility_rental: "Facility Rental",
  breakage_fee: "Breakage Fee",
  pta: "PTA",
  chapel_service: "Chapel Service Income",
  development_fee: "Development Fee",
  other_income: "Other Income",
};

const SCHOOL_INFO = {
  name: "Messiah Mission Institute",
  location: "Morris Farm, Paynesville",
  contact: "0840 0824",
};

const FeesSummary = () => {
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()].value);
  const [rows, setRows] = useState([]);
  const [expenditures, setExpenditures] = useState([]);
    const [schoolFeeTotal, setSchoolFeeTotal] = useState(0);
    const [tuitionTotal, setTuitionTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payrollTotal, setPayrollTotal] = useState(0);
   
  const [totals, setTotals] = useState({}); // fee-type totals (all time)
  const [expTotals, setExpTotals] = useState({}); // expenditure totals (all time)
  const [overallTotals, setOverallTotals] = useState({
    overall_fee_total: 0,
    overall_expenditure_total: 0,
    overall_net_total: 0,
  });

  const pdfRef = useRef(); // printable element ref

  useEffect(() => {
    fetchTotals();
    fetchRows(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch fee-type totals, expenditure totals, and overall totals
  const fetchTotals = async () => {
    try {
      const access = localStorage.getItem("access");
      // fee-type totals (existing)
      try {
        const feeTypeRes = await api.get("fee-type/totals/", {
          headers: { Authorization: `Bearer ${access}` },
        });
        setTotals(feeTypeRes.data || {});
      } catch (err) {
        console.warn("fee-type/totals/ fetch failed", err);
      }

      // expenditure totals (all time) - new
      try {
        const expTotalRes = await api.get("expenditure/totals/", {
          headers: { Authorization: `Bearer ${access}` },
        });
        setExpTotals(expTotalRes.data || {});
        setPayrollTotal(Number(expTotalRes.data.payroll_total || 0));
       
      } catch (err) {
        console.warn("expenditure/totals/ fetch failed", err);
      }

      // overall totals from financial-summary endpoint (existing)
      try {
        const finRes = await api.get("financial-summary/", {
          headers: { Authorization: `Bearer ${access}` },
        });

        setOverallTotals({
          overall_fee_total: Number(finRes.data.overall_fee_total || 0),
          overall_expenditure_total: Number(finRes.data.overall_expenditure_total || 0),
          overall_net_total: Number(finRes.data.overall_net_total || 0),
        });
      } catch (err2) {
        // If financial-summary not available, ignore but log
        console.warn("financial-summary fetch failed", err2);
      }
    } catch (err) {
      console.error("Error loading totals", err);
    }
  };

  // fetch fees + expenditures for selected month
    const fetchRows = async (month) => {
      setLoading(true);
      setError("");
      try {
        const access = localStorage.getItem("access");
        const res = await api.get(`fees/by-month/?month=${month}`, {
          headers: { Authorization: `Bearer ${access}` },
        });
  
        const feesArray = Array.isArray(res.data?.fees) ? res.data.fees : [];
        const expendituresArray = Array.isArray(res.data?.expenditures) ? res.data.expenditures : [];
  
        const normalizedFees = feesArray.map((r) => ({
          ...r,
          fee_charge: Number(r.fee_charge || 0),
          amount: Number(r.amount || 0),
          balance: Number(r.balance || 0),
          school_fee_total: Number(r.school_fee_total || 0),
          tuition_total: Number(r.tuition_total || 0),
        }));
  
        const normalizedExps = expendituresArray.map((e) => ({
          ...e,
          amount: Number(e.amount || 0),
        }));
  
        setRows(normalizedFees);
        setExpenditures(normalizedExps);
  
        setSchoolFeeTotal(Number(res.data.school_fee_total || 0));
        setTuitionTotal(Number(res.data.tuition_total || 0));
        setPayrollTotal(Number(res.data.payroll_total || 0));
        
      } catch (err) {
        console.error(err);
        setError("Failed to load entries for selected month.");
      } finally {
        setLoading(false);
      }
    };

  const handleMonthChange = (e) => {
    const m = e.target.value;
    setSelectedMonth(m);
    fetchRows(m);
  };

  // Helper to format currency
  const fmt = (v) =>
    typeof v === "number"
      ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Excel export using SheetJS — now includes expenditures and expenditure totals (all time)
  const exportToExcel = () => {
    if ((!rows || rows.length === 0) && (!expenditures || expenditures.length === 0)) {
      setError("No data to export for the selected month.");
      return;
    }

    const headerRows = [
      [SCHOOL_INFO.name],
      [SCHOOL_INFO.location],
      [`Contact: ${SCHOOL_INFO.contact}`],
      [],
      [`Month: ${MONTHS.find((x) => x.value === selectedMonth)?.label}`],
      [],
    ];

    // Fees sheet content
 const feeColumns = [
  "Student Name",
  "Grade Class",
  "Fee Type",
  "Fee Charge",
  "Amount Paid",
  "Balance",
  "School Fee Total",
  "Tuition Total",
  "Date Paid",
  "Remarks",
  "Created By",
];

const feeRows = (rows || []).map((r) => [
  r.student_name,
  r.grade_class,
  FEE_TYPE_LABELS[r.fee_type] || r.fee_type,
  Number(r.fee_charge).toFixed(2),
  Number(r.amount).toFixed(2),
  Number(r.balance).toFixed(2),
  Number(schoolFeeTotal || 0).toFixed(2),
  Number(tuitionTotal || 0).toFixed(2),
  r.date_paid ? new Date(r.date_paid).toLocaleDateString() : "",
  r.remarks || "",
  r.created_by || "",
]);

    const expColumns = ["Category", "Amount", "Remarks", "Date", "Created By"];
    const expRows = (expenditures || []).map((e) => [
      e.category_label || e.category,
      Number(e.amount).toFixed(2),
      e.remarks || "",
      e.date ? new Date(e.date).toLocaleDateString() : "",
      e.created_by || "",
    ]);

    // Build combined worksheet data (single sheet: Fees first, then Expenditures)
    let worksheetData = [
      ...headerRows,
      ["FEES (MONTHLY)"],
      feeColumns,
      ...feeRows,
      [],
      // Fee totals (all time)
      ["FEE TOTALS (ALL TIME)"],
      ["Type", "Amount"],
    ];

    // Add fee-type totals rows
    Object.keys(FEE_TYPE_LABELS).forEach((k) => {
      worksheetData.push([FEE_TYPE_LABELS[k], fmt(totals[k] || 0)]);
    });

// Add School Fee Total 
const totalSchoolFee = Number(schoolFeeTotal || 0);
worksheetData.push(["School Fee Total", fmt(totalSchoolFee)]);

// Add Tuition Total
const totalTuition = Number(tuitionTotal || 0);
worksheetData.push(["Tuition Total", fmt(totalTuition)]);


    worksheetData.push([]);
    worksheetData.push([
  "TOTAL INCOME",
  fmt(Object.values(totals || {}).reduce((s, v) => s + Number(v || 0), 0) + totalSchoolFee + tuitionTotal)
]);
    worksheetData.push([]);
    worksheetData.push([]);

    // Expenditures monthly
    worksheetData.push(["EXPENDITURES (MONTHLY)"]);
    worksheetData.push(expColumns);
    worksheetData.push(...expRows);
    worksheetData.push([]);

    // Expenditure totals (all time)
    worksheetData.push(["EXPENDITURE TOTALS (ALL TIME)"]);
    worksheetData.push(["Category", "Amount"]);
    Object.entries(expTotals.totals_per_category || {}).forEach(([cat, amt]) => {
      worksheetData.push([cat, fmt(amt)]);
    });

 

    worksheetData.push([]);
    worksheetData.push(["OVERALL EXPENDITURE TOTAL", fmt(expTotals.overall_total || 0)]);
    worksheetData.push([]);
    worksheetData.push([]);

    // Overall totals (include month label in the label cell)
    worksheetData.push(["OVERALL TOTALS"]);
    worksheetData.push([
      "Overall Fee Total for the month - " + (MONTHS.find((m) => m.value === selectedMonth)?.label || ""),
      fmt(overallTotals.overall_fee_total),
    ]);
    worksheetData.push(["Overall Expenditure Total", fmt(overallTotals.overall_expenditure_total)]);
    worksheetData.push(["Net Total (Fees - Expenditures)", fmt(overallTotals.overall_net_total)]);

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // column widths (best effort)
ws["!cols"] = [
  { wch: 30 }, // Student
  { wch: 18 }, // Grade
  { wch: 20 }, // Fee Type
  { wch: 12 }, // Fee Charge
  { wch: 12 }, // Amount
  { wch: 12 }, // Balance
  { wch: 14 }, // School Fee Total
  { wch: 14 }, // Tuition Total
  { wch: 15 }, // Date Paid
  { wch: 30 }, // Remarks
  { wch: 18 }, // Created By
];


    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Fees_${selectedMonth}`);

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const filename = `Messiah_Fees_${selectedMonth}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  // HTML -> PDF export using html2pdf (prints hidden pdfRef element)
  const exportToPdf = async () => {
    if ((!rows || rows.length === 0) && (!expenditures || expenditures.length === 0)) {
      setError("No data to export for the selected month.");
      return;
    }

    try {
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default || html2pdfModule;

      // options tuned for A4 portrait with some margin; image quality high
      const opt = {
        margin: 0.4,
        filename: `Messiah_Fees_${selectedMonth}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          windowHeight: document.body.scrollHeight,
        },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      };

      const element = pdfRef.current;
      if (!element) {
        setError("Cannot find the printable element for PDF generation.");
        return;
      }

      html2pdf()
        .set(opt)
        .from(element)
        .save();
    } catch (err) {
      console.error("Failed to generate PDF", err);
      setError("PDF generation failed. Make sure html2pdf.js is installed.");
    }
  };

  // Prepare totals display array (fee types present in FEE_TYPE_LABELS)
  const totalsEntries = Object.keys(FEE_TYPE_LABELS).map((k) => ({
    key: k,
    label: FEE_TYPE_LABELS[k],
    amount: Number(totals[k] || 0),
  }));

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
      {/* HIDDEN printable area for html2pdf */}
      <Box sx={{ display: "none" }}>
        <div
          ref={pdfRef}
          id="pdf-content"
          style={{
            width: "210mm",
            minHeight: "297mm",
            padding: "20mm",
            boxSizing: "border-box",
            fontFamily: '"Inter", "Helvetica", Arial, sans-serif',
            color: "#222",
            backgroundColor: "#fff",
          }}
        >
          {/* Page styling for page breaks */}
          <style>{`
  .page-break { display: block !important; page-break-before: always !important; break-before: page !important; }

  /* Keep titles attached to their content */
  .no-break { page-break-inside: avoid !important; break-inside: avoid !important; }
  .section-title { page-break-after: avoid !important; break-after: avoid !important; }
  .table-keep { page-break-inside: avoid !important; break-inside: avoid !important; }

  /* Avoid splitting table rows */
  table, tr, td, th { page-break-inside: avoid !important; break-inside: avoid !important; }

  #pdf-content { overflow: visible !important; }
`}</style>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <img
              src={logo}
              alt="School Logo"
              style={{ width: 80, height: 80, objectFit: "contain", marginRight: 16 }}
            />
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{SCHOOL_INFO.name}</div>
              <div style={{ fontSize: 12, color: "#555" }}>{SCHOOL_INFO.location}</div>
              <div style={{ fontSize: 12, color: "#555" }}>Contact: {SCHOOL_INFO.contact}</div>
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "2px solid #e6e9ef", margin: "8px 0 16px 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                Monthly Fee Report - {MONTHS.find((m) => m.value === selectedMonth)?.label}
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                Generated: {new Date().toLocaleString()}
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#666" }}>Prepared by: Admin</div>
            </div>
          </div>

          {/* Totals / Income Section (Fees - All time) */}
          <div style={{ marginBottom: 14 }} className="no-break">
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Totals (All time)</div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>INCOME</th>
                  <th style={{ textAlign: "right", padding: 6, borderBottom: "1px solid #ddd" }}>TOTAL</th>
                </tr>
              </thead>
               <tbody>
  {totalsEntries.map((t) => (
    <tr key={t.key}>
      <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6" }}>{t.label}</td>
      <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6", textAlign: "right" }}>
        ${fmt(t.amount)}
      </td>
    </tr>
  ))}
  {/* School Fee Total row */}
<tr>
  <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6" }}>School Fee Total</td>
  <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6", textAlign: "right" }}>
    ${fmt(Number(schoolFeeTotal || 0))}
  </td>
</tr>

<tr>
  <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6" }}>Tuition Total</td>
  <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6", textAlign: "right" }}>
    ${fmt(Number(tuitionTotal || 0))}
  </td>
</tr>

  {/* Grand total row */}
  <tr>
    <td style={{ padding: 8, fontWeight: 800, borderTop: "2px solid #e6e9ef" }}>TOTAL INCOME</td>
    <td style={{ padding: 8, fontWeight: 800, borderTop: "2px solid #e6e9ef", textAlign: "right" }}>
     ${fmt(
  Object.values(totals || {}).reduce((s, v) => s + Number(v || 0), 0) +
  Number(schoolFeeTotal || 0) +
  Number(tuitionTotal || 0)
)}
    </td>
  </tr>
</tbody>

            </table>
          </div>

          {/* FEES Data table (monthly) */}
<div style={{ marginTop: 8 }} className="section no-break">
  <div className="section-title" style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>
    Fees
  </div>

  {/* Main Fees Table */}
  <table
    className="table-keep"
    style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}
  >
    <thead>
      <tr>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Student</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Grade</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Fee Type</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>Charge</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>Amount</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>Balance</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Date Paid</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Remarks</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Created By</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td style={{ border: "1px solid #eee", padding: 8 }}>{r.student_name}</td>
          <td style={{ border: "1px solid #eee", padding: 8 }}>{r.grade_class}</td>
          <td style={{ border: "1px solid #eee", padding: 8 }}>{FEE_TYPE_LABELS[r.fee_type] || r.fee_type}</td>
          <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(r.fee_charge)}</td>
          <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(r.amount)}</td>
          <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(r.balance)}</td>
          <td style={{ border: "1px solid #eee", padding: 8 }}>
            {r.date_paid ? new Date(r.date_paid).toLocaleDateString() : ""}
          </td>
          <td style={{ border: "1px solid #eee", padding: 8 }}>{r.remarks}</td>
          <td style={{ border: "1px solid #eee", padding: 8 }}>{r.created_by}</td>
        </tr>
      ))}
    </tbody>
  </table>

  {/* Totals Table */}
  <table
    className="table-keep"
    style={{
      width: "50%",
      marginTop: 8,
      borderCollapse: "collapse",
      fontSize: 11,
      float: "right", // Align totals table to the right
    }}
  >
    <thead>
      <tr>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>School Fee Total</th>
        <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>Tuition Total</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(schoolFeeTotal || 0)}</td>
        <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(tuitionTotal || 0)}</td>
      </tr>
    </tbody>
  </table>
</div>


          {/* Page break -> Expenditure Totals (All time) on Page 2 */}
          <div className="page-break" style={{ marginTop: 20 }} />

          {/* Expenditure Totals (All Time) - Page 2 */}
          <div style={{ marginTop: 6 }} className="no-break">
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Expenditure Totals (All Time)</div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>CATEGORY</th>
                  <th style={{ textAlign: "right", padding: 6, borderBottom: "1px solid #ddd" }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(expTotals.totals_per_category || {}).length === 0 ? (
                  <tr>
                    <td style={{ padding: 8 }} colSpan={2}>
                      No expenditure totals available.
                    </td>
                  </tr>
                ) : (
                  Object.entries(expTotals.totals_per_category || {}).map(([cat, amt]) => (
                    <tr key={cat}>
                      <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6" }}>{cat}</td>
                      <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6", textAlign: "right" }}>${fmt(amt)}</td>
                    </tr>
                    
                  ))
                )}

             
 
                 <tr>
    <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6" }}>Payroll Total</td>
    <td style={{ padding: 8, borderBottom: "1px solid #f1f3f6", textAlign: "right" }}>
      ${fmt(payrollTotal || 0)}
    </td>
  </tr>


  


                <tr>
                  <td style={{ padding: 8, fontWeight: 800, borderTop: "2px solid #e6e9ef" }}>OVERALL EXPENDITURE TOTAL</td>
                  <td style={{ padding: 8, fontWeight: 800, borderTop: "2px solid #e6e9ef", textAlign: "right" }}>
                    ${fmt(expTotals.overall_total || 0)}
                  </td>
                </tr>
              </tbody>

            </table>
          </div>

          {/* Page break -> Monthly Expenditures on Page 3 */}
          <div className="page-break" style={{ marginTop: 20 }} />

          {/* Monthly Expenditures table - Page 3 */}
          <div style={{ marginTop: 18 }} className="section no-break">
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Expenditures</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Category</th>
                  <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc", textAlign: "right" }}>
                    Amount
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Date</th>
                  <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Remarks</th>
                  <th style={{ border: "1px solid #ddd", padding: 8, background: "#f7f9fc" }}>Created By</th>
                </tr>
              </thead>
              <tbody>
                {expenditures.map((e) => (
                  <tr key={e.id}>
                    <td style={{ border: "1px solid #eee", padding: 8 }}>{e.category_label || e.category}</td>
                    <td style={{ border: "1px solid #eee", padding: 8, textAlign: "right" }}>${fmt(e.amount)}</td>
                    <td style={{ border: "1px solid #eee", padding: 8 }}>{e.date ? new Date(e.date).toLocaleDateString() : ""}</td>
                    <td style={{ border: "1px solid #eee", padding: 8 }}>{e.remarks}</td>
                    <td style={{ border: "1px solid #eee", padding: 8 }}>{e.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
  <TableCell colSpan={6} sx={{ fontWeight: 700 }}>Payroll Total</TableCell>
  <TableCell sx={{ textAlign: "right" }}>${fmt(payrollTotal || 0)}</TableCell>
  <TableCell colSpan={4}></TableCell>
</TableRow>

          </div>

          {/* Page break -> Overall Totals on Page 4 */}
          <div className="page-break" style={{ marginTop: 20 }} />

          {/* Totals (overall fee, overall expenditure, net) - Page 4 */}
          <div style={{ marginTop: 18 }} className="no-break">
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Overall Totals for {MONTHS.find((m) => m.value === selectedMonth)?.label}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <tbody>
                <tr>
                  <td style={{ padding: 8 }}>Overall Fee Total</td>
                  <td style={{ padding: 8, textAlign: "right" }}>${fmt(overallTotals.overall_fee_total)}</td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Overall Expenditure Total</td>
                  <td style={{ padding: 8, textAlign: "right" }}>${fmt(overallTotals.overall_expenditure_total)}</td>
                </tr>
                <tr>
                  <td style={{ padding: 8, fontWeight: 800 }}>Net Total (Fees - Expenditures)</td>
                  <td style={{ padding: 8, textAlign: "right", fontWeight: 800 }}>${fmt(overallTotals.overall_net_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ marginTop: 20, fontSize: 11, color: "#666", display: "flex", justifyContent: "space-between" }}>
            <div>Generated by: School Financial System</div>
            <div>Page 4</div>
          </div>
        </div>
      </Box>

      {/* Visible UI */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#203447" }}>
            Monthly Fee Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#617085", mt: 0.5 }}>
            {SCHOOL_INFO.name} — {SCHOOL_INFO.location}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select labelId="month-select-label" value={selectedMonth} label="Month" onChange={handleMonthChange}>
              {MONTHS.map((m) => (
                <MenuItem value={m.value} key={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => fetchRows(selectedMonth)}
            sx={{ backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#125ca1" } }}
          >
            Refresh
          </Button>

          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToExcel} sx={{ borderColor: "#1976d2", color: "#1976d2" }}>
            Export Excel
          </Button>

          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportToPdf} sx={{ borderColor: "#d32f2f", color: "#d32f2f" }}>
            Export PDF
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Totals (All time)
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
              These are overall totals by fee type (not limited to the selected month).
            </Typography>
            <MonthlyAnalysic/>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              {Object.keys(FEE_TYPE_LABELS).map((k) => (
                <Box
                  key={k}
                  sx={{
                    minWidth: 140,
                    background: "#fff",
                    p: 1.2,
                    borderRadius: 1.5,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    {FEE_TYPE_LABELS[k]}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2" }}>
                    ${totals[k] ? Number(totals[k]).toLocaleString() : "0.00"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Entries for {MONTHS.find((m) => m.value === selectedMonth)?.label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" title="Table view">
              <TableViewIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={48} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ color: "#6b7280" }}>No entries found for this month.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Student</b>
                  </TableCell>
                  <TableCell>
                    <b>Grade</b>
                  </TableCell>
                  <TableCell>
                    <b>Fee Type</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>Charge</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>Amount</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>Balance</b>
                  </TableCell>
           
                  <TableCell>
                    <b>Date Paid</b>
                  </TableCell>
                  <TableCell>
                    <b>Remarks</b>
                  </TableCell>
                  <TableCell>
                    <b>Created By</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.student_name}</TableCell>
                    <TableCell>{r.grade_class}</TableCell>
                    <TableCell>{FEE_TYPE_LABELS[r.fee_type] || r.fee_type}</TableCell>
                    <TableCell align="right">${Number(r.fee_charge).toFixed(2)}</TableCell>
                    <TableCell align="right">${Number(r.amount).toFixed(2)}</TableCell>
                    <TableCell align="right">${Number(r.balance).toFixed(2)}</TableCell>
                    <TableCell>{r.date_paid ? new Date(r.date_paid).toLocaleDateString() : ""}</TableCell>
                    <TableCell>{r.remarks}</TableCell>
                    <TableCell>{r.created_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>

<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
  <TableCell colSpan={6} sx={{ fontWeight: 700 }}>School Fee Total</TableCell>
  <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(schoolFeeTotal)}</TableCell>
  <TableCell colSpan={4}></TableCell>
</TableRow>
<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
  <TableCell colSpan={6} sx={{ fontWeight: 700 }}>Tuition Total</TableCell>
  <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(tuitionTotal)}</TableCell>
  <TableCell colSpan={4}></TableCell>
</TableRow>

<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
  <TableCell colSpan={6} sx={{ fontWeight: 700 }}>Payroll Total</TableCell>
  <TableCell sx={{ textAlign: "right" }}>${fmt(payrollTotal || 0)}</TableCell>
  <TableCell colSpan={4}></TableCell>
</TableRow>


            </Table>
          </TableContainer>
          
        )}
      </Paper>
    </Box>
  );
};

export default FeesSummary;